import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, ChevronLeft, ChevronRight, RotateCcw, Trash2, UserX } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { possibleGarbageAPI } from "../api";
import { useCampus, useSchool } from "../contexts";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import type { PossibleGarbageStatus, PossibleGarbageUser } from "../types/possibleGarbage";

const PAGE_LIMIT = 50;
const DEADLINE_OPTIONS = [
  { hours: 24, label: "24 hours" },
  { hours: 48, label: "48 hours" },
  { hours: 24 * 7, label: "7 days" },
];

const STATUS_CARDS: { status: Exclude<PossibleGarbageStatus, "all">; label: string; description: string }[] = [
  { status: "not_warned", label: "Not warned", description: "No photo, nobody told them yet" },
  { status: "warned", label: "Warned", description: "Deactivated at the deadline if still no photo" },
  { status: "deactivated", label: "Deactivated", description: "Turned off for no photo, can be restored" },
];

type Action = "warn" | "deactivate" | "restore";

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : "-");

const StatusBadge = ({ user }: { user: PossibleGarbageUser }) => {
  if (user.status === "deactivated") return <Badge variant="destructive">Deactivated</Badge>;
  if (user.status === "warned") return <Badge variant="secondary">Deactivates {formatDate(user.deadline)}</Badge>;
  return <Badge variant="outline">Not warned</Badge>;
};

/**
 * Students without a profile photo. Every action applies to the rows ticked on
 * this page and nothing else: warn (push + deadline, after which the backend
 * deactivates them if they still have no photo), deactivate now, or restore.
 * Deactivation is soft — the account is switched off, not deleted.
 */
export function PossibleGarbagePage() {
  const queryClient = useQueryClient();
  const { selectedSchool } = useSchool();
  const { selectedCampus } = useCampus();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState<PossibleGarbageStatus>("all");
  const [onboarded, setOnboarded] = useState<"any" | "yes" | "no">("any");
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pending, setPending] = useState<Action | null>(null);
  const [hours, setHours] = useState<number | null>(null);

  const schoolId = selectedSchool?._id;
  const campusId = selectedCampus?._id;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any change to what is listed drops the selection, so an action can't hit rows no longer on screen.
  useEffect(() => {
    setSelected(new Set());
  }, [schoolId, campusId, status, onboarded, searchTerm, currentPage]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["possibleGarbage", schoolId, campusId, status, onboarded, searchTerm, currentPage],
    queryFn: () =>
      possibleGarbageAPI.list({
        page: currentPage,
        limit: PAGE_LIMIT,
        search: searchTerm || undefined,
        status,
        onboarded: onboarded === "any" ? undefined : onboarded === "yes",
        schoolId,
        campusId,
      }),
    placeholderData: keepPreviousData,
  });

  const mutation = useMutation({
    mutationFn: async (action: Action) => {
      const ids = [...selected];
      if (action === "warn") {
        const result = await possibleGarbageAPI.warn(ids, hours ?? data?.default_grace_hours ?? 24);
        return `Warned ${result.warned} student(s), ${result.pushed} push(es) sent`;
      }
      if (action === "deactivate") {
        const result = await possibleGarbageAPI.deactivate(ids);
        return `Deactivated ${result.deactivated} student(s)`;
      }
      const result = await possibleGarbageAPI.restore(ids);
      return `Restored ${result.restored} student(s)`;
    },
    onSuccess: (message) => {
      toast.success(message);
      setSelected(new Set());
      setPending(null);
      queryClient.invalidateQueries({ queryKey: ["possibleGarbage"] });
      queryClient.invalidateQueries({ queryKey: ["incompleteProfiles"] });
    },
    onError: () => toast.error("Action failed. Nothing was changed for the failed request."),
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState message="Loading students without a photo..." />
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <ErrorState message="Failed to load students without a photo" />
      </DashboardLayout>
    );
  }

  const { users, pagination, summary } = data;
  const deadlineHours = hours ?? data.default_grace_hours;
  const allOnPageSelected = users.length > 0 && users.every((u) => selected.has(u._id));
  const selectedUsers = users.filter((u) => selected.has(u._id));
  const canRestore = selectedUsers.length > 0 && selectedUsers.every((u) => u.status === "deactivated");
  const canWarnOrDeactivate = selectedUsers.length > 0 && selectedUsers.every((u) => u.status !== "deactivated");

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => setSelected(allOnPageSelected ? new Set() : new Set(users.map((u) => u._id)));

  const confirmCopy: Record<Action, { title: string; body: string; button: string }> = {
    warn: {
      title: `Warn ${selected.size} student(s)?`,
      body: `They get a push: "Add a profile photo within ${deadlineHours} hours to keep your Walky account active." Anyone still without a photo at the deadline is deactivated automatically.`,
      button: "Send warning",
    },
    deactivate: {
      title: `Deactivate ${selected.size} student(s) now?`,
      body: "They can't sign in until restored. Nothing is deleted, and Restore brings the account back as it was.",
      button: "Deactivate",
    },
    restore: {
      title: `Restore ${selected.size} student(s)?`,
      body: "Their accounts are switched back on and the warning is cleared.",
      button: "Restore",
    },
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Possible Garbage"
          description={`Students without a profile photo. ${summary.onboarded} of the active ones finished onboarding; the rest stopped before the photo step.`}
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {STATUS_CARDS.map((card) => {
            const active = status === card.status;
            return (
              <button
                key={card.status}
                type="button"
                onClick={() => {
                  setStatus(active ? "all" : card.status);
                  setCurrentPage(1);
                }}
                aria-pressed={active}
                className="text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Card className={active ? "border-primary ring-1 ring-primary" : "hover:border-primary/50"}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{card.label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{summary[card.status]}</div>
                    <p className="text-xs text-muted-foreground">{card.description}</p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>

        <Card>
          <CardContent className="pt-6 flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="max-w-sm"
            />
            <Select
              value={onboarded}
              onValueChange={(value) => {
                setOnboarded(value as "any" | "yes" | "no");
                setCurrentPage(1);
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Onboarding: any</SelectItem>
                <SelectItem value="yes">Finished onboarding</SelectItem>
                <SelectItem value="no">Didn't finish onboarding</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <Select value={String(deadlineHours)} onValueChange={(value) => setHours(Number(value))}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEADLINE_OPTIONS.map((option) => (
                  <SelectItem key={option.hours} value={String(option.hours)}>
                    Deadline: {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button disabled={!canWarnOrDeactivate} onClick={() => setPending("warn")}>
              <BellRing className="h-4 w-4" />
              Warn
            </Button>
            <Button variant="destructive" disabled={!canWarnOrDeactivate} onClick={() => setPending("deactivate")}>
              <Trash2 className="h-4 w-4" />
              Deactivate now
            </Button>
            <Button variant="outline" disabled={!canRestore} onClick={() => setPending("restore")}>
              <RotateCcw className="h-4 w-4" />
              Restore
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="h-5 w-5" />
              Students without a photo
            </CardTitle>
            <CardDescription>
              {selected.size} selected · showing {users.length} of {pagination.total}
              {isFetching ? " · updating…" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAll} aria-label="Select page" />
                    </TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Onboarded</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Last active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        Nobody here
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>
                          <Checkbox
                            checked={selected.has(user._id)}
                            onCheckedChange={() => toggle(user._id)}
                            aria-label={`Select ${user.first_name}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-xs text-muted-foreground">{user.email}</div>
                        </TableCell>
                        <TableCell>{user.school_name || "-"}</TableCell>
                        <TableCell>{user.is_onboarded ? "Yes" : "No"}</TableCell>
                        <TableCell>
                          <StatusBadge user={user} />
                        </TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                        <TableCell>{formatDate(user.last_active_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={currentPage >= pagination.pages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        {pending && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{confirmCopy[pending].title}</DialogTitle>
              <DialogDescription>{confirmCopy[pending].body}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPending(null)} disabled={mutation.isPending}>
                Cancel
              </Button>
              <Button
                variant={pending === "deactivate" ? "destructive" : "default"}
                onClick={() => mutation.mutate(pending)}
                disabled={mutation.isPending}
              >
                {confirmCopy[pending].button}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </DashboardLayout>
  );
}
